import { useCallback, useEffect, useMemo, useState } from 'react'
import { haalMonitoring, type Dag, type Monitoring as Data } from './api'
import { naamVan } from './namen'
import { MODULES } from '../content/content'
import { kioskNaam, verstuur, wachtend, zetKioskNaam } from '../kiosk/meting'

const VERVERS = 30_000
const KIOSKS = ['1', '2', '3', 'reserve']

const leeg = (): Dag => ({ kliks: 0, sessies: 0, duur: 0, doelen: {}, uren: Array(24).fill(0) })

function telOp(a: Dag, b: Dag) {
  a.kliks += b.kliks
  a.sessies += b.sessies
  a.duur += b.duur
  for (const [k, v] of Object.entries(b.doelen)) a.doelen[k] = (a.doelen[k] ?? 0) + v
  b.uren.forEach((v, i) => { a.uren[i] += v })
}

const datum = (d: string) =>
  new Date(`${d}T12:00:00`).toLocaleDateString('nl-NL', { weekday: 'short', day: 'numeric', month: 'short' })

const duur = (s: number) => (s < 60 ? `${Math.round(s)} s` : `${Math.floor(s / 60)} min ${Math.round(s % 60)} s`)

/** Een rij met een balk: label, balk, aantal en aandeel. */
function Balk({ label, waarde, max, totaal }: { label: string; waarde: number; max: number; totaal: number }) {
  const deel = totaal > 0 ? Math.round((waarde / totaal) * 100) : 0
  return (
    <li className="mon-rij" title={`${label}: ${waarde} (${deel}%)`}>
      <span className="mon-naam">{label}</span>
      <span className="mon-spoor"><span className="mon-balk" style={{ width: `${max > 0 ? (waarde / max) * 100 : 0}%` }} /></span>
      <span className="mon-getal">{waarde}</span>
      <span className="mon-deel">{deel}%</span>
    </li>
  )
}

/**
 * Waar bezoekers op tikken (docs/beheer.md). De cijfers komen van alle kiosks
 * samen, per dag of over de hele beurs, en verversen elke halve minuut.
 */
export function Monitoring({ token, onUitgelogd, inKiosk = false }: {
  token: string
  onUitgelogd: () => void
  /** In de kiosk: ook de naam van deze kiosk instellen en de wachtrij zien. */
  inKiosk?: boolean
}) {
  const [data, setData] = useState<Data | null>(null)
  const [fout, setFout] = useState<string | null>(null)
  const [dag, setDag] = useState<string>('alle')
  const [kiosk, setKiosk] = useState<string>('alle')
  const [eigenNaam, setEigenNaam] = useState(kioskNaam)
  const [opgehaald, setOpgehaald] = useState<Date | null>(null)

  const haal = useCallback(async () => {
    try {
      // Eerst wat deze kiosk nog heeft liggen, dan staat dat er ook bij.
      if (inKiosk) await verstuur()
      const d = await haalMonitoring(token)
      if (d === null) { onUitgelogd(); return }
      setData(d)
      setFout(null)
      setOpgehaald(new Date())
    } catch {
      setFout('Ophalen lukte niet. Is er verbinding?')
    }
  }, [token, onUitgelogd, inKiosk])

  useEffect(() => {
    void haal()
    const tik = window.setInterval(() => void haal(), VERVERS)
    return () => window.clearInterval(tik)
  }, [haal])

  const dagen = useMemo(
    () => [...new Set((data?.totalen ?? []).flatMap((t) => Object.keys(t.dagen)))].sort().reverse(),
    [data],
  )
  const kiosks = useMemo(() => [...new Set((data?.totalen ?? []).map((t) => t.kiosk || '?'))].sort(), [data])

  const samen = useMemo(() => {
    const s = leeg()
    for (const t of data?.totalen ?? []) {
      if (kiosk !== 'alle' && (t.kiosk || '?') !== kiosk) continue
      for (const [d, v] of Object.entries(t.dagen)) if (dag === 'alle' || d === dag) telOp(s, v)
    }
    return s
  }, [data, dag, kiosk])

  const ranglijst = useMemo(
    () => Object.entries(samen.doelen).sort((a, b) => b[1] - a[1]).slice(0, 25),
    [samen],
  )
  const tegels = useMemo(
    () => MODULES.map((m) => ({ naam: m.naam.nl, n: samen.doelen[`tegel:${m.id}`] ?? 0 })).sort((a, b) => b.n - a.n),
    [samen],
  )
  const tegelTotaal = tegels.reduce((a, t) => a + t.n, 0)
  // De uren met tikken, en altijd de beursdag van 9 tot 18.
  const uren = samen.uren
    .map((n, uur) => ({ uur, n }))
    .filter(({ uur, n }) => n > 0 || (uur >= 9 && uur <= 17))
  const uurMax = Math.max(1, ...uren.map((u) => u.n))

  const perKiosk = useMemo(() => {
    const m = new Map<string, { kliks: number; sessies: number; bijgewerkt: number }>()
    for (const t of data?.totalen ?? []) {
      const naam = t.kiosk || '?'
      const r = m.get(naam) ?? { kliks: 0, sessies: 0, bijgewerkt: 0 }
      for (const [d, v] of Object.entries(t.dagen)) {
        if (dag !== 'alle' && d !== dag) continue
        r.kliks += v.kliks
        r.sessies += v.sessies
      }
      r.bijgewerkt = Math.max(r.bijgewerkt, t.bijgewerkt)
      m.set(naam, r)
    }
    return [...m.entries()].sort()
  }, [data, dag])

  const kiesEigenNaam = (n: string) => {
    zetKioskNaam(n)
    setEigenNaam(n)
  }

  return (
    <div className="mon">
      {data?.lokaal && (
        <p className="mon-noot">
          Lokaal: alleen de cijfers van deze kiosk. Ze gaan ook naar {data.lokaal.publiek.replace(/^https?:\/\//, '')}/beheer.html
          zodra er internet is; daar staan alle kiosks samen.
        </p>
      )}
      {data?.proef && (
        <p className="mon-noot">Preview-omgeving: dit zijn testcijfers, los van die van de beurs.</p>
      )}

      <div className="mon-filters">
        <label>
          <span>Dag</span>
          <select value={dag} onChange={(e) => setDag(e.target.value)}>
            <option value="alle">Alle dagen</option>
            {dagen.map((d) => <option key={d} value={d}>{datum(d)}</option>)}
          </select>
        </label>
        <label>
          <span>Kiosk</span>
          <select value={kiosk} onChange={(e) => setKiosk(e.target.value)}>
            <option value="alle">Alle kiosks</option>
            {kiosks.map((k) => <option key={k} value={k}>{k === '?' ? 'zonder naam' : `Kiosk ${k}`}</option>)}
          </select>
        </label>
        <button type="button" className="mon-ververs" onClick={() => void haal()}>Ververs</button>
      </div>

      {fout && <p className="mon-noot mon-fout">{fout}</p>}
      {!data && !fout && <p className="mon-noot">Laden…</p>}

      {data && (
        <>
          <div className="mon-tegels">
            <div className="mon-kpi"><span>Tikken</span><b>{samen.kliks}</b></div>
            <div className="mon-kpi"><span>Bezoekers (sessies)</span><b>{samen.sessies}</b></div>
            <div className="mon-kpi">
              <span>Gem. sessieduur</span>
              <b>{samen.sessies > 0 ? duur(samen.duur / samen.sessies) : '–'}</b>
            </div>
            <div className="mon-kpi">
              <span>Tikken per bezoeker</span>
              <b>{samen.sessies > 0 ? (samen.kliks / samen.sessies).toFixed(1) : '–'}</b>
            </div>
          </div>

          <section className="mon-blok">
            <h3>Meest getikt</h3>
            {ranglijst.length === 0
              ? <p className="mon-noot">Nog geen tikken in deze selectie.</p>
              : (
                <ol className="mon-lijst">
                  {ranglijst.map(([doel, n]) => (
                    <Balk key={doel} label={naamVan(doel)} waarde={n} max={ranglijst[0][1]} totaal={samen.kliks} />
                  ))}
                </ol>
              )}
          </section>

          <section className="mon-blok">
            <h3>Gekozen onderdelen op het hoofdscherm</h3>
            <ol className="mon-lijst">
              {tegels.map((t) => (
                <Balk key={t.naam} label={t.naam} waarde={t.n} max={tegels[0].n} totaal={tegelTotaal} />
              ))}
            </ol>
          </section>

          <section className="mon-blok">
            <h3>Tikken per uur</h3>
            <div className="mon-uren" role="img" aria-label="Tikken per uur van de dag">
              {uren.map(({ uur, n }) => (
                <div key={uur} className="mon-uur" title={`${uur}:00–${uur + 1}:00: ${n} tikken`}>
                  <span className="mon-uur-getal">{n > 0 ? n : ''}</span>
                  <span className="mon-kolom" style={{ height: `${(n / uurMax) * 100}%` }} />
                  <span className="mon-uur-label">{uur}</span>
                </div>
              ))}
            </div>
          </section>

          <section className="mon-blok">
            <h3>Per kiosk</h3>
            <table className="mon-tabel">
              <thead><tr><th>Kiosk</th><th>Tikken</th><th>Sessies</th><th>Laatst gemeld</th></tr></thead>
              <tbody>
                {perKiosk.map(([naam, r]) => (
                  <tr key={naam}>
                    <td>{naam === '?' ? 'zonder naam' : `Kiosk ${naam}`}</td>
                    <td>{r.kliks}</td>
                    <td>{r.sessies}</td>
                    <td>{new Date(r.bijgewerkt).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>
        </>
      )}

      {inKiosk && (
        <section className="mon-blok">
          <h3>Deze kiosk</h3>
          <div className="mon-keuze">
            {KIOSKS.map((k) => (
              <button
                key={k}
                type="button"
                className={eigenNaam === k ? 'mon-aan' : ''}
                onClick={() => kiesEigenNaam(k)}
              >
                {k === 'reserve' ? 'Reserve' : `Kiosk ${k}`}
              </button>
            ))}
          </div>
          <p className="mon-noot">
            Geef elke kiosk een eigen nummer, anders lopen de cijfers door elkaar. Nog te versturen: {wachtend()} tikken.
          </p>
        </section>
      )}

      {opgehaald && (
        <p className="mon-noot">Bijgewerkt om {opgehaald.toLocaleTimeString('nl-NL')}; ververst elke 30 seconden.</p>
      )}
    </div>
  )
}
